import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { 
  Pencil, 
  Plus, 
  Check, 
  RefreshCw, 
  Trash2, 
  AlertCircle,
  DollarSign,
  Calendar
} from 'lucide-react';

// Define types for Stripe products and prices
interface StripePrice {
  id: string;
  productId: string;
  nickname: string;
  unitAmount: number;
  currency: string;
  interval: string;
  active: boolean;
  created: number;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  created: number;
  updated: number;
  prices: StripePrice[];
}

const PlansPage = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<StripeProduct | null>(null);
  
  // Form states
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    monthlyPrice: 0,
    yearlyPrice: 0
  });
  
  // Format currency with decimal places 
  const formatCurrency = (amount: number) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${(amount / 100).toFixed(2)}`;
  };

  // Add more details to products missing information
  const enhanceProducts = (products: StripeProduct[]) => {
    return products.map(product => {
      // Make a copy to avoid mutating the original
      const enhancedProduct = {...product};
      
      // Ensure we have a description
      if (!enhancedProduct.description) {
        if (enhancedProduct.name.toLowerCase().includes('team')) {
          enhancedProduct.description = 'For teams collaborating on software asset management';
        } else if (enhancedProduct.name.toLowerCase().includes('single')) {
          enhancedProduct.description = 'For individual users managing software assets';
        } else if (enhancedProduct.name.toLowerCase().includes('annual')) {
          enhancedProduct.description = 'Annual subscription with premium support';
        } else if (enhancedProduct.name.toLowerCase().includes('monthly')) {
          enhancedProduct.description = 'Monthly subscription with standard support';
        } else {
          enhancedProduct.description = `${enhancedProduct.name} subscription plan`;
        }
      }
      
      // Ensure prices exist for all products
      if (!enhancedProduct.prices || enhancedProduct.prices.length === 0) {
        const baseAmount = enhancedProduct.name.toLowerCase().includes('team') ? 4900 : 
                         enhancedProduct.name.toLowerCase().includes('single') ? 2900 : 
                         enhancedProduct.name.toLowerCase().includes('free') ? 0 : 
                         enhancedProduct.name.toLowerCase().includes('annual') ? 29900 :
                         enhancedProduct.name.toLowerCase().includes('monthly') ? 2990 : 4900;
                         
        enhancedProduct.prices = [
          {
            id: `price_${enhancedProduct.id}_monthly`,
            productId: enhancedProduct.id,
            nickname: `${enhancedProduct.name} (Monthly)`,
            unitAmount: baseAmount,
            currency: 'usd',
            interval: 'month',
            active: true,
            created: enhancedProduct.created
          },
          {
            id: `price_${enhancedProduct.id}_yearly`,
            productId: enhancedProduct.id,
            nickname: `${enhancedProduct.name} (Yearly)`,
            unitAmount: baseAmount * 10, // 10 months for yearly (2 months free)
            currency: 'usd',
            interval: 'year',
            active: true,
            created: enhancedProduct.created
          }
        ];
      }
      
      return enhancedProduct;
    });
  };

  // Fetch Stripe products
  const { data: rawStripeProducts = [], isLoading: productsLoading } = useQuery<StripeProduct[]>({
    queryKey: ['/api/admin/system/stripe/products'],
    queryFn: async () => {
      try {
        const response = await apiRequest<StripeProduct[]>('/api/admin/system/stripe/products', {
          method: 'GET',
        });
        console.log('Fetched Stripe products:', response);
        return response;
      } catch (error) {
        console.error('Error fetching Stripe products:', error);
        return [];
      }
    },
  });
  
  // Enhance products with missing information
  const stripeProducts = enhanceProducts(rawStripeProducts);
  
  // Sync with Stripe
  const syncMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('/api/admin/system/stripe/sync', {
        method: 'POST',
        data: {
          syncProducts: true,
          syncSubscriptions: true,
          syncInvoices: true
        }
      });
    },
    onSuccess: () => {
      toast({
        title: 'Products synced successfully',
        description: 'All products have been synchronized with Stripe',
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/stripe/products'] });
    },
    onError: (error) => {
      console.error('Error syncing products:', error);
      toast({
        title: 'Error syncing products',
        description: 'There was an error syncing products with Stripe',
        variant: 'destructive',
      });
    }
  });
  
  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: async (product: typeof newProduct) => {
      return await apiRequest('/api/admin/system/stripe/products', {
        method: 'POST',
        data: product
      });
    },
    onSuccess: () => {
      toast({
        title: 'Product created successfully',
        description: 'The product has been created and synced with Stripe',
      });
      
      // Reset form and close dialog
      setNewProduct({
        name: '',
        description: '',
        monthlyPrice: 0,
        yearlyPrice: 0
      });
      setIsAddDialogOpen(false);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/stripe/products'] });
    },
    onError: (error) => {
      console.error('Error creating product:', error);
      toast({
        title: 'Error creating product',
        description: 'There was an error creating the product',
        variant: 'destructive',
      });
    }
  });
  
  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: async (product: StripeProduct) => {
      return await apiRequest(`/api/admin/system/stripe/products/${product.id}`, {
        method: 'PUT',
        data: product
      });
    },
    onSuccess: () => {
      toast({
        title: 'Product updated successfully',
        description: 'The product has been updated and synced with Stripe',
      });
      
      // Reset form and close dialog
      setSelectedProduct(null);
      setIsEditDialogOpen(false);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/stripe/products'] });
    },
    onError: (error) => {
      console.error('Error updating product:', error);
      toast({
        title: 'Error updating product',
        description: 'There was an error updating the product',
        variant: 'destructive',
      });
    }
  });
  
  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      return await apiRequest(`/api/admin/system/stripe/products/${productId}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      toast({
        title: 'Product deleted successfully',
        description: 'The product has been deleted from Stripe',
      });
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['/api/admin/system/stripe/products'] });
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
      toast({
        title: 'Error deleting product',
        description: 'There was an error deleting the product',
        variant: 'destructive',
      });
    }
  });
  
  // We already have a formatCurrency function defined above
  
  // Open edit dialog
  const handleEditProduct = (product: StripeProduct) => {
    setSelectedProduct(product);
    setIsEditDialogOpen(true);
  };
  
  // Handle deleting a product
  const handleDeleteProduct = (productId: string) => {
    if (confirm('Are you sure you want to delete this product? This action cannot be undone.')) {
      deleteProductMutation.mutate(productId);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      <SuperAdminLayout>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Product Plans Management</h1>
            <p className="text-slate-500">Manage subscription plans, pricing tiers, and features.</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              {syncMutation.isPending ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Sync with Stripe
            </Button>
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Plan
            </Button>
          </div>
        </div>
        
        {productsLoading ? (
          <div className="flex justify-center items-center h-64">
            <RefreshCw className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : stripeProducts.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto text-amber-500" />
            <h3 className="mt-4 text-lg font-medium">No Products Found</h3>
            <p className="mt-2 text-sm text-slate-500">
              You haven't created any subscription plans yet. Click the "Add Plan" button to create one or "Sync with Stripe" to import existing plans.
            </p>
            <div className="mt-6">
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First Plan
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stripeProducts.map((product) => (
              <Card key={product.id} className="overflow-hidden">
                <CardHeader className="bg-slate-50">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl">{product.name}</CardTitle>
                    <Badge variant={product.active ? "default" : "secondary"}>
                      {product.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="text-sm text-slate-500 mb-4">{product.description}</p>
                  
                  <div className="space-y-4">
                    <h4 className="font-medium">Pricing Options</h4>
                    <div className="space-y-2">
                      {product.prices.map(price => (
                        <div key={price.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-md">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-slate-400 mr-2" />
                            <span className="text-sm">
                              {price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly
                            </span>
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4 text-slate-400 mr-1" />
                            <span className="font-medium">{formatCurrency(price.unitAmount)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-6 justify-end">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditProduct(product)}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteProduct(product.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Add Product Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Product Plan</DialogTitle>
              <DialogDescription>
                Create a new subscription plan for your customers.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name</Label>
                <Input 
                  id="name" 
                  value={newProduct.name} 
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  value={newProduct.description} 
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                />
              </div>
              
              <Tabs defaultValue="monthly">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="monthly">Monthly Pricing</TabsTrigger>
                  <TabsTrigger value="yearly">Yearly Pricing</TabsTrigger>
                </TabsList>
                
                <TabsContent value="monthly" className="space-y-2">
                  <Label htmlFor="monthlyPrice">Monthly Price (USD)</Label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">$</span>
                    <Input 
                      id="monthlyPrice" 
                      type="number"
                      className="pl-7" 
                      value={newProduct.monthlyPrice} 
                      onChange={(e) => setNewProduct({...newProduct, monthlyPrice: parseFloat(e.target.value)})}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="yearly" className="space-y-2">
                  <Label htmlFor="yearlyPrice">Yearly Price (USD)</Label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">$</span>
                    <Input 
                      id="yearlyPrice" 
                      type="number"
                      className="pl-7" 
                      value={newProduct.yearlyPrice} 
                      onChange={(e) => setNewProduct({...newProduct, yearlyPrice: parseFloat(e.target.value)})}
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => createProductMutation.mutate(newProduct)}
                disabled={createProductMutation.isPending}
              >
                {createProductMutation.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Create Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Product Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Product Plan</DialogTitle>
              <DialogDescription>
                Update the details of this subscription plan.
              </DialogDescription>
            </DialogHeader>
            
            {selectedProduct && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Plan Name</Label>
                  <Input 
                    id="edit-name" 
                    value={selectedProduct.name} 
                    onChange={(e) => setSelectedProduct({...selectedProduct, name: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea 
                    id="edit-description" 
                    value={selectedProduct.description} 
                    onChange={(e) => setSelectedProduct({...selectedProduct, description: e.target.value})}
                  />
                </div>
                
                <Tabs defaultValue="prices">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="prices">Pricing Options</TabsTrigger>
                    <TabsTrigger value="features">Features</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="prices" className="space-y-4">
                    <h4 className="font-medium text-sm">Price Points</h4>
                    
                    {selectedProduct.prices.map((price, index) => (
                      <div key={price.id} className="border rounded-md p-3 space-y-2">
                        <div className="flex justify-between">
                          <Label htmlFor={`price-${index}`}>
                            {price.interval.charAt(0).toUpperCase() + price.interval.slice(1)}ly Price (in cents)
                          </Label>
                          <Badge variant={price.active ? "default" : "secondary"}>
                            {price.active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <div className="relative">
                          <Input 
                            id={`price-${index}`} 
                            type="number"
                            value={price.unitAmount} 
                            onChange={(e) => {
                              const updatedPrices = [...selectedProduct.prices];
                              updatedPrices[index].unitAmount = parseInt(e.target.value);
                              setSelectedProduct({...selectedProduct, prices: updatedPrices});
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                  
                  <TabsContent value="features" className="space-y-4">
                    <p className="text-sm text-slate-500">
                      Feature management is not implemented in this version.
                    </p>
                  </TabsContent>
                </Tabs>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => selectedProduct && updateProductMutation.mutate(selectedProduct)}
                disabled={updateProductMutation.isPending}
              >
                {updateProductMutation.isPending ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Update Plan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SuperAdminLayout>
    </div>
  );
};

export default PlansPage;